import TInterviewThread from "./TInterviewThread";
import {TJobStatusResponse, TSendMessageResponse, TTranscribeVoiceMessageResponse} from "./TInterviewThreadApi";
import InterviewThreadApiError, {TInterviewThreadApiErrorKind} from "./InterviewThreadApiError";
import {
    normalizeInterviewThread,
    normalizeJobStatusResponse,
    normalizeSendMessageResponse,
    normalizeTranscriptionResponse,
} from "./InterviewThreadNormalizer";

type TEndpoints = {
    thread?: string,
    sendMessage?: string,
    transcribeVoiceMessage?: string,
    synthesizeVoiceMessage?: string,
    processingJobStatus?: string,
    requeueProcessingJob?: string,
};

type TFetch = (input: RequestInfo|URL, init?: RequestInit) => Promise<Response>;

export default class InterviewThreadApi {

    constructor(
        private readonly endpoints: TEndpoints,
        private readonly fetchRequest: TFetch = (input, init) => fetch(input, init),
    ) {
    }

    public async getThread(signal?: AbortSignal): Promise<TInterviewThread> {
        const data = await this.requestJson(
            this.requireEndpoint("thread"),
            {method: "GET", signal},
        );

        return normalizeInterviewThread(data);
    }

    public async sendMessage(
        interviewId: number,
        content: string,
        testMode: boolean,
        clientMessageId: string,
        signal?: AbortSignal,
    ): Promise<TSendMessageResponse> {
        const data = await this.requestJson(
            this.requireEndpoint("sendMessage"),
            {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({interviewId, content, testMode, clientMessageId}),
                signal,
            },
        );

        return normalizeSendMessageResponse(data);
    }

    public async transcribeVoiceMessage(
        threadId: number,
        audio: Blob,
        filename: string,
        signal?: AbortSignal,
    ): Promise<TTranscribeVoiceMessageResponse> {
        const formData = new FormData();
        formData.append("threadId", String(threadId));
        formData.append("audio", audio, filename);
        const data = await this.requestJson(
            this.requireEndpoint("transcribeVoiceMessage"),
            {method: "POST", body: formData, signal},
        );

        return normalizeTranscriptionResponse(data);
    }

    public async synthesizeVoiceMessage(
        threadId: number,
        messageId: number,
        signal?: AbortSignal,
    ): Promise<Blob> {
        const response = await this.performRequest(
            this.requireEndpoint("synthesizeVoiceMessage"),
            {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({threadId, messageId}),
                signal,
            },
        );
        if (!response.ok) {
            const body = await this.readJson(response);
            throw this.createHttpError(response.status, body);
        }

        return response.blob();
    }

    public async getProcessingJobStatus(
        jobId: number,
        signal?: AbortSignal,
    ): Promise<TJobStatusResponse> {
        const data = await this.requestJson(
            this.buildJobUrl(this.requireEndpoint("processingJobStatus"), jobId),
            {method: "GET", signal},
        );

        return normalizeJobStatusResponse(data);
    }

    public async requeueProcessingJob(
        jobId: number,
        signal?: AbortSignal,
    ): Promise<TJobStatusResponse> {
        const data = await this.requestJson(
            this.buildJobUrl(this.requireEndpoint("requeueProcessingJob"), jobId),
            {method: "POST", signal},
        );

        return normalizeJobStatusResponse(data);
    }

    private requireEndpoint(name: keyof TEndpoints): string {
        const url = this.endpoints[name];
        if (!url) {
            throw new InterviewThreadApiError({
                kind: "validation",
                message: `InterviewThread api missing endpoint "${name}".`,
                retryable: false,
            });
        }

        return url;
    }

    private buildJobUrl(urlTemplate: string, jobId: number): string {
        if (!urlTemplate.includes("{jobId}")) {
            throw new InterviewThreadApiError({
                kind: "validation",
                message: "InterviewThread job endpoint is missing the {jobId} placeholder.",
                retryable: false,
            });
        }

        return urlTemplate.replace("{jobId}", encodeURIComponent(String(jobId)));
    }

    private async requestJson(url: string, init: RequestInit): Promise<unknown> {
        const response = await this.performRequest(url, init);
        const body = await this.readJson(response);
        if (!response.ok) {
            throw this.createHttpError(response.status, body);
        }
        if (body === null) {
            throw new InterviewThreadApiError({
                kind: "validation",
                message: "The server returned an invalid JSON response.",
                status: response.status,
                retryable: false,
            });
        }

        return body;
    }

    private async performRequest(url: string, init: RequestInit): Promise<Response> {
        try {
            return await this.fetchRequest(url, init);
        } catch (error) {
            if (init.signal?.aborted) {
                throw error;
            }

            throw new InterviewThreadApiError({
                kind: "network",
                message: "The server could not be reached.",
                retryable: true,
            });
        }
    }

    private async readJson(response: Response): Promise<unknown|null> {
        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    private createHttpError(status: number, body: unknown): InterviewThreadApiError {
        const message = typeof body === "object"
            && body !== null
            && "error" in body
            && typeof body.error === "string"
            ? body.error
            : `The server returned HTTP status ${status}.`;
        let kind: TInterviewThreadApiErrorKind = "server";
        if (status === 400 || status === 422) {
            kind = "validation";
        } else if (status === 403) {
            kind = "forbidden";
        } else if (status === 404) {
            kind = "notFound";
        } else if (status === 408 || status === 504) {
            kind = "timeout";
        }

        return new InterviewThreadApiError({
            kind,
            message,
            status,
            retryable: kind === "server" || kind === "timeout",
        });
    }

}
